'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { getCsrfTokenFromCookie } from '@/lib/api';
import { API_URL, API_BASE } from '@/config/api.config';
import { 
  Package, 
  CheckCircle2, 
  Lock, 
  Clock,
  Settings,
  Loader2,
  TrendingUp,
  Zap,
  Shield,
  ArrowRight,
} from 'lucide-react';

interface App {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  category: string;
  requiredPlan: string;
  isComingSoon: boolean;
  isEnabled: boolean;
  isInstalled: boolean;
  hasAccess: boolean;
  lastUsedAt: string | null;
  usageCount: number;
}

interface Organization {
  id: string;
  name: string;
}

export default function MyAppsPage() {
  const router = useRouter();
  const [apps, setApps] = useState<App[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      fetchApps();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${API_BASE}/organizations`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch organizations');

      const data = await response.json();
      setOrganizations(data);
      if (data.length > 0) {
        setSelectedOrg(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/organizations/${selectedOrg}/apps`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error('Failed to fetch apps');

      const data = await response.json();
      setApps(data);
    } catch (error) {
      console.error('Error fetching apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableApp = async (appId: string) => {
    try {
      setActionLoading(appId);
      const csrfToken = getCsrfTokenFromCookie();
      const response = await fetch(
        `${API_BASE}/organizations/${selectedOrg}/apps/${appId}/install`,
        {
          method: 'POST',
          headers: {
            ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const error = await response.json();
        if (error.error === 'Upgrade required') {
          alert(`This app requires ${error.requiredPlan} plan. Please upgrade your subscription.`);
          router.push('/pricing');
          return;
        }
        throw new Error('Failed to enable app');
      }

      await fetchApps();
    } catch (error) {
      console.error('Error enabling app:', error);
      alert('Failed to enable app');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableApp = async (appId: string) => {
    try {
      setActionLoading(appId);
      const csrfToken = getCsrfTokenFromCookie();
      const response = await fetch(
        `${API_BASE}/organizations/${selectedOrg}/apps/${appId}/disable`,
        {
          method: 'POST',
          headers: {
            ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
          },
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error('Failed to disable app');

      await fetchApps();
    } catch (error) {
      console.error('Error disabling app:', error);
      alert('Failed to disable app');
    } finally {
      setActionLoading(null);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'COMPLIANCE':
        return <Shield className="h-5 w-5 text-blue-600" />;
      case 'PRIVACY':
        return <Lock className="h-5 w-5 text-purple-600" />;
      case 'SECURITY':
        return <Zap className="h-5 w-5 text-red-600" />;
      case 'OPERATIONS':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      default:
        return <Package className="h-5 w-5 text-gray-600" />;
    }
  };

  const getPlanBadge = (plan: string) => {
    const badges = {
      FREE: 'bg-green-100 text-green-800 border-green-200',
      PROFESSIONAL: 'bg-blue-100 text-blue-800 border-blue-200',
      ENTERPRISE: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return badges[plan as keyof typeof badges] || badges.FREE;
  };

  if (loading && !selectedOrg) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Apps</h1>
          <p className="text-gray-600">
            Manage your compliance and privacy apps for each organization
          </p>
        </div>

        {/* Organization Selector */}
        {organizations.length > 1 && (
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Organization
            </label>
            <select
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Apps</p>
                <p className="text-3xl font-bold text-gray-900">
                  {apps.filter((a) => a.isEnabled).length}
                </p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Available Apps</p>
                <p className="text-3xl font-bold text-gray-900">
                  {apps.filter((a) => a.hasAccess && !a.isComingSoon).length}
                </p>
              </div>
              <Package className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Coming Soon</p>
                <p className="text-3xl font-bold text-gray-900">
                  {apps.filter((a) => a.isComingSoon).length}
                </p>
              </div>
              <Clock className="h-10 w-10 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Apps Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app) => (
              <div
                key={app.id}
                className={`bg-white rounded-lg shadow border-2 transition-all ${
                  app.isEnabled
                    ? 'border-green-500'
                    : app.hasAccess && !app.isComingSoon
                    ? 'border-gray-200 hover:border-blue-300'
                    : 'border-gray-200 opacity-75'
                }`}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-4xl">{app.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900">{app.name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          {getCategoryIcon(app.category)}
                          <span className="text-xs text-gray-500">{app.category}</span>
                        </div>
                      </div>
                    </div>
                    {app.isEnabled && (
                      <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0" />
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4">{app.description}</p>

                  {/* Plan Badge */}
                  <div className="mb-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPlanBadge(
                        app.requiredPlan
                      )}`}
                    >
                      {app.requiredPlan}
                    </span>
                  </div>

                  {/* Usage Stats */}
                  {app.isEnabled && app.usageCount > 0 && (
                    <div className="mb-4 text-xs text-gray-500">
                      <p>Used {app.usageCount} times</p>
                      {app.lastUsedAt && (
                        <p>Last used: {new Date(app.lastUsedAt).toLocaleDateString()}</p>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {app.isComingSoon ? (
                      <button
                        disabled
                        className="w-full px-4 py-2 bg-gray-100 text-gray-500 rounded-lg cursor-not-allowed flex items-center justify-center"
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        Coming Soon
                      </button>
                    ) : !app.hasAccess ? (
                      <button
                        onClick={() => router.push('/pricing')}
                        className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center"
                      >
                        <Lock className="h-4 w-4 mr-2" />
                        Upgrade to Unlock
                      </button>
                    ) : app.isEnabled ? (
                      <>
                        <button
                          onClick={() => router.push(`/organizations/${selectedOrg}/apps/${app.slug}`)}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Configure
                        </button>
                        <button
                          onClick={() => handleDisableApp(app.id)}
                          disabled={actionLoading === app.id}
                          className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 flex items-center justify-center"
                        >
                          {actionLoading === app.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Disable'
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEnableApp(app.id)}
                        disabled={actionLoading === app.id}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                      >
                        {actionLoading === app.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <ArrowRight className="h-4 w-4 mr-2" />
                            Enable App
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && apps.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No apps available</h3>
            <p className="text-gray-600">Check back soon for new compliance and privacy apps!</p>
          </div>
        )}
      </div>
    </div>
  );
}
